import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import PlanSelection from "@/components/PlanSelection";
import { useRouteLoaderData } from "react-router-dom";

import PlanTiersList from "@/components/PlanTiersList/PlanTiersList";

interface UpgradePlanDialogProps {
  open: boolean;
  onClose?: () => void;
}

function UpgradePlanDialog({ open, onClose }: UpgradePlanDialogProps) {
  const { subscription } = useRouteLoaderData("main") as any;
  const theme = useTheme();

  const totalCustomers = subscription?.totalCustomers || 0;
  const maxCustomersLimit = subscription?.maxCustomersLimit || 0;
  const currentPrice = subscription?.price || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h2" fontWeight={600}>
            Time to Upgrade!
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 4,
          py: 4,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            borderRight: { md: "1px solid" },
            borderColor: { md: "grey.200" },
            pr: { md: 4 },
          }}
        >
          <Box>
            <Typography variant="h3" fontWeight={600} sx={{ mb: 2 }}>
              Your Business is Growing! 🎉
            </Typography>

            <Typography variant="p1" color="grey.90" sx={{ mb: 3 }}>
              Fantastic news! You've added more customers to your store. Since
              tinyAlbert charges based on your subscriber count, it's time to
              upgrade your plan to match your growing audience.
            </Typography>

            <Typography variant="h3" sx={{ py: 4 }}>
              Pricing Plans
            </Typography>
            <PlanTiersList currentPrice={currentPrice} />
          </Box>
          <Box
            sx={{
              mt: "auto",
            }}
          >
            <Typography variant="p3" color="grey.90">
              <strong>Need help choosing?</strong> Our team is happy to help.{" "}
              <a
                href="mailto:support@tinyalbert.ai"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                }}
              >
                support@tinyalbert.ai
              </a>
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, pl: { md: 4 } }}>
          <Typography variant="h3" fontWeight={600} sx={{ mb: 2 }}>
            Choose Your Plan
          </Typography>

          {!subscription?.sameSubscription && (
            <Box
              sx={{
                bgcolor: "grey.20",
                borderRadius: 2,
                p: 2.5,
                mb: 3,
                border: "1px solid",
                borderColor: "primary.main",
              }}
            >
              <Typography
                variant="p2"
                color="primary.dark"
                sx={{ display: "block", mb: 1 }}
              >
                <strong>Recommended Plan</strong>
              </Typography>
              <Typography variant="p2" color="grey.90" sx={{ mb: 1.5 }}>
                With{" "}
                <strong>{totalCustomers.toLocaleString()} customers</strong>,
                you'll need the <strong>${currentPrice}/month</strong> package,
                which includes up to{" "}
                <strong>{maxCustomersLimit.toLocaleString()} customers</strong>.
              </Typography>
              <Typography variant="p3" color="grey.90">
                <a
                  href="https://tinyalbert.ai/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  Learn More About Pricing →
                </a>
              </Typography>
            </Box>
          )}

          <Box>
            <PlanSelection
              plans={[
                {
                  ...subscription,
                  maxCustomerCount: subscription.maxCustomersLimit,
                },
              ]}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradePlanDialog;
